import { format, isToday, isYesterday } from "date-fns";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Mail as MailIcon, 
  MailOpen, 
  Paperclip, 
  User, 
  MessageSquare,
  Send 
} from "lucide-react";
import { Mail, MailThread } from "./types";
import { useMail } from "./use-mail";
import { toPlainText } from "@/lib/rich-text";
import { useRef, useEffect } from "react";

interface MailListProps {
  mails: Mail[];
  threads?: MailThread[];
  isLoading: boolean;
  useThreads?: boolean;
  isSentFolder?: boolean;
}

function formatMailDate(dateString: string) {
  const date = new Date(dateString);
  if (isToday(date)) {
    return format(date, "h:mm a");
  }
  if (isYesterday(date)) {
    return "Yesterday";
  }
  return format(date, "MMM d");
}

function getDisplayName(mail: Mail, isSent: boolean = false): string {
  if (isSent) {
    // For sent emails, show recipient
    return mail.recipient_email || mail.to_email || "Unknown";
  }
  if (mail.candidates) {
    const name = [mail.candidates.first_name, mail.candidates.last_name]
      .filter(Boolean)
      .join(" ");
    if (name) return name;
  }
  if (mail.from_name) return mail.from_name;
  return mail.from_email.split("<")[0].trim() || mail.from_email;
}

export function MailList({ 
  mails, 
  threads, 
  isLoading, 
  useThreads = false,
  isSentFolder = false 
}: MailListProps) {
  const { 
    selectedMail, 
    setSelectedMail, 
    selectedThread, 
    setSelectedThread,
    isMultiSelectMode,
    selectedEmailIds,
    toggleEmailSelection,
  } = useMail();
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedMail?.id, selectedThread?.id]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-2 p-3 border rounded-lg">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (useThreads && threads) {
    if (threads.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-muted-foreground">
          <MailIcon className="h-12 w-12 mb-3 opacity-30" />
          <p>No emails found</p>
        </div>
      );
    }

    return (
      <ScrollArea className="h-full">
        <div ref={listRef} className="flex flex-col gap-1 p-2">
          {threads.map((thread) => {
            const isSelected = selectedThread?.id === thread.id;
            const hasUnread = thread.unreadCount > 0;
            const latestMailId = thread.latestMail.id;
            const isChecked = selectedEmailIds.has(latestMailId);

            return (
              <div
                key={thread.id}
                className={cn(
                  "flex items-start gap-2 rounded-lg border p-3 text-left text-sm transition-all hover:bg-accent",
                  isSelected && !isMultiSelectMode && "bg-muted ring-1 ring-primary",
                  hasUnread && "bg-primary/5 border-primary/20",
                  isChecked && "bg-primary/10 border-primary/30"
                )}
              >
                {isMultiSelectMode && (
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => toggleEmailSelection(latestMailId)}
                    className="mt-1 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <button
                  ref={isSelected ? selectedRef : null}
                  onClick={() => {
                    if (isMultiSelectMode) {
                      toggleEmailSelection(latestMailId);
                    } else {
                      setSelectedThread(thread);
                    }
                  }}
                  className="flex flex-col items-start gap-1 flex-1 text-left"
                >
                  <div className="flex w-full items-center gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {hasUnread ? (
                        <MailIcon className="h-4 w-4 text-primary shrink-0" />
                      ) : (
                        <MailOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <span
                        className={cn(
                          "font-medium truncate",
                          hasUnread && "font-semibold"
                        )}
                      >
                        {getDisplayName(thread.latestMail, isSentFolder)}
                      </span>
                      {thread.totalCount > 1 && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0 shrink-0">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {thread.totalCount}
                        </Badge>
                      )}
                    </div>
                    <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
                      {formatMailDate(thread.latestMail.received_at)}
                    </span>
                  </div>
                  <div className="text-sm font-medium truncate w-full">
                    {thread.subject}
                  </div>
                  <div className="line-clamp-2 text-xs text-muted-foreground w-full">
                    {toPlainText(thread.latestMail.body_text || thread.latestMail.body_html)?.substring(0, 120) || "No preview available"}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {thread.latestMail.candidate_id && (
                      <Badge variant="outline" className="text-xs px-1.5 py-0">
                        <User className="h-3 w-3 mr-1" />
                        Candidate
                      </Badge>
                    )}
                    {thread.latestMail.inbound_email_attachments &&
                      thread.latestMail.inbound_email_attachments.length > 0 && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0">
                          <Paperclip className="h-3 w-3 mr-1" />
                          {thread.latestMail.inbound_email_attachments.length}
                        </Badge>
                      )}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    );
  }

  // Non-threaded view
  if (mails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-muted-foreground">
        <MailIcon className="h-12 w-12 mb-3 opacity-30" />
        <p>No emails found</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div ref={listRef} className="flex flex-col gap-1 p-2">
        {mails.map((mail) => {
          const isSelected = selectedMail?.id === mail.id;
          const isChecked = selectedEmailIds.has(mail.id);

          return (
            <div
              key={mail.id}
              className={cn(
                "flex items-start gap-2 rounded-lg border p-3 text-left text-sm transition-all hover:bg-accent",
                isSelected && !isMultiSelectMode && "bg-muted ring-1 ring-primary",
                !mail.is_read && "bg-primary/5 border-primary/20",
                isChecked && "bg-primary/10 border-primary/30"
              )}
            >
              {isMultiSelectMode && (
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={() => toggleEmailSelection(mail.id)}
                  className="mt-1 shrink-0"
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <button
                ref={isSelected ? selectedRef : null}
                onClick={() => {
                  if (isMultiSelectMode) {
                    toggleEmailSelection(mail.id);
                  } else {
                    setSelectedMail(mail);
                  }
                }}
                className="flex flex-col items-start gap-1 flex-1 text-left"
              >
                <div className="flex w-full items-center gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isSentFolder ? (
                      <Send className="h-4 w-4 text-muted-foreground shrink-0" />
                    ) : mail.is_read ? (
                      <MailOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                    ) : (
                      <MailIcon className="h-4 w-4 text-primary shrink-0" />
                    )}
                    <span
                      className={cn(
                        "font-medium truncate",
                        !mail.is_read && !isSentFolder && "font-semibold"
                      )}
                    >
                      {getDisplayName(mail, isSentFolder)}
                    </span>
                  </div>
                  <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
                    {formatMailDate(mail.sent_at || mail.received_at)}
                  </span>
                </div>
                <div className="text-sm font-medium truncate w-full">
                  {mail.subject || "(No Subject)"}
                </div>
                <div className="line-clamp-2 text-xs text-muted-foreground w-full">
                  {toPlainText(mail.body_text || mail.body_html)?.substring(0, 120) || "No preview available"}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  {mail.candidate_id && (
                    <Badge variant="outline" className="text-xs px-1.5 py-0">
                      <User className="h-3 w-3 mr-1" />
                      Candidate
                    </Badge>
                  )}
                  {mail.inbound_email_attachments &&
                    mail.inbound_email_attachments.length > 0 && (
                      <Badge variant="secondary" className="text-xs px-1.5 py-0">
                        <Paperclip className="h-3 w-3 mr-1" />
                        {mail.inbound_email_attachments.length}
                      </Badge>
                    )}
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
